import React, { useEffect, useState, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { getMaterialUrl } from '../../config/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Scale,
  CoreScaleOptions,
  LineController,
  BarController,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface PriceData {
  date: string;
  p2nt119: string;
  PE6PP32: string;
  PE100RC: string | null;
  HM0359PE: string;
  HM0349PE: string;
  HD12500LB: string;
  HD10530LB: string;
  HD07580SB: string;
  HD03594PE: string;
  HD03580SB: string;
  HD03490NP: string;
  plast27673: string;
  stavrolen27383: string;
  plast02651: string;
}

const parsePrice = (priceStr: string | null): number | null => {
  if (!priceStr || priceStr === 'N/A' || priceStr === '*') return null;
  const cleanStr = priceStr.replace(/\s+/g, '');
  if (cleanStr.includes('-')) {
    const [min, max] = cleanStr.split('-').map(Number);
    return Math.max(min, max);
  }
  return Number(cleanStr);
};

const DynamicsPe: React.FC = () => {
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [selectedRange, setSelectedRange] = useState<[number, number]>([0, 1]);
  const [activeHandle, setActiveHandle] = useState<'left' | 'right' | null>(null);
  const [showAllData, setShowAllData] = useState(true);
  const [datasetVisibility, setDatasetVisibility] = useState<Record<string, boolean>>({});
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const access = localStorage.getItem('access');
        const response = await fetch(getMaterialUrl('pe'), {
          headers: access ? { 'Authorization': `Bearer ${access}` } : {}
        });
        const data = await response.json();
        setPriceData(data);

        const initialVisibility: Record<string, boolean> = {
          'Trend Line': true,
          'ПЭ2НТ11-9': true,
          'PE6PP32': true,
          'PE100RC': true,
          'HM 0359 PE': true,
          'HM0349PE': true,
          'HD12500LB': true,
          'HD10530LB': true,
          'HD07580SB': true,
          'HD03594PE': true,
          'HD03580SB': true,
          'HD03490NP': true,
          'plast27673': true,
          'stavrolen27383': true,
          'plast02651': true
        };
        setDatasetVisibility(initialVisibility);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const toggleDatasetVisibility = (datasetKey: string) => {
    setDatasetVisibility(prev => ({
      ...prev,
      [datasetKey]: !prev[datasetKey]
    }));
  };

  const toggleAllDatasets = () => {
    const newShowAllData = !showAllData;
    setShowAllData(newShowAllData);
    const newVisibility: Record<string, boolean> = {};
    Object.keys(datasetVisibility).forEach(key => {
      newVisibility[key] = newShowAllData;
    });
    setDatasetVisibility(newVisibility);
  };

  const calculateTrend = (data: PriceData) => {
    const p2nt119 = parsePrice(data.p2nt119);
    const hm0359pe = parsePrice(data.HM0359PE);

    if (p2nt119 === null && hm0359pe === null) return null;
    if (p2nt119 === null) return hm0359pe;
    if (hm0359pe === null) return p2nt119;

    return Math.max(p2nt119, hm0359pe);
  };

  const handleMouseDown = (e: React.MouseEvent, handle: 'left' | 'right') => {
    setActiveHandle(handle);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!activeHandle || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = Math.max(0, Math.min(1, x / rect.width));

    if (activeHandle === 'left') {
      setSelectedRange([position, Math.max(position, selectedRange[1])]);
    } else {
      setSelectedRange([Math.min(position, selectedRange[0]), position]);
    }
  };

  const handleMouseUp = () => {
    setActiveHandle(null);
  };

  const filteredData = priceData.slice(
    Math.floor(selectedRange[0] * priceData.length),
    Math.ceil(selectedRange[1] * priceData.length)
  );

  const chartData = {
    labels: filteredData.map(item => new Date(item.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Trend Line',
        data: filteredData.map(calculateTrend),
        borderColor: 'rgb(0, 0, 0)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        type: 'line' as const,
        yAxisID: 'y',
        borderWidth: 2,
        tension: 0.1,
        spanGaps: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        datalabels: {
          display: false
        },
        fill: false,
        hidden: !datasetVisibility['Trend Line']
      },
      {
        label: 'ПЭ2НТ11-9',
        data: filteredData.map(item => parsePrice(item.p2nt119)),
        backgroundColor: 'rgba(255, 0, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['ПЭ2НТ11-9']
      },
      {
        label: 'PE6PP32',
        data: filteredData.map(item => parsePrice(item.PE6PP32)),
        backgroundColor: 'rgba(0, 128, 255, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['PE6PP32']
      },
      {
        label: 'PE100RC',
        data: filteredData.map(item => parsePrice(item.PE100RC)),
        backgroundColor: 'rgba(255, 0, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['PE100RC'],
        order: 1
      },
      {
        label: 'HM 0359 PE',
        data: filteredData.map(item => parsePrice(item.HM0359PE)),
        backgroundColor: 'rgba(0, 255, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HM 0359 PE']
      },
      {
        label: 'HM0349PE',
        data: filteredData.map(item => parsePrice(item.HM0349PE)),
        backgroundColor: 'rgba(128, 0, 128, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HM0349PE']
      },
      {
        label: 'HD12500LB',
        data: filteredData.map(item => parsePrice(item.HD12500LB)),
        backgroundColor: 'rgba(255, 0, 255, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HD12500LB']
      },
      {
        label: 'HD10530LB',
        data: filteredData.map(item => parsePrice(item.HD10530LB)),
        backgroundColor: 'rgba(0, 255, 255, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HD10530LB']
      },
      {
        label: 'HD07580SB',
        data: filteredData.map(item => parsePrice(item.HD07580SB)),
        backgroundColor: 'rgba(255, 69, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HD07580SB']
      },
      {
        label: 'HD03594PE',
        data: filteredData.map(item => parsePrice(item.HD03594PE)),
        backgroundColor: 'rgba(0, 128, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HD03594PE']
      },
      {
        label: 'HD03580SB',
        data: filteredData.map(item => parsePrice(item.HD03580SB)),
        backgroundColor: 'rgba(128, 128, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HD03580SB']
      },
      {
        label: 'HD03490NP',
        data: filteredData.map(item => parsePrice(item.HD03490NP)),
        backgroundColor: 'rgba(128, 0, 0, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['HD03490NP']
      },
      {
        label: 'plast27673',
        data: filteredData.map(item => parsePrice(item.plast27673)),
        backgroundColor: 'rgba(0, 0, 128, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['plast27673']
      },
      {
        label: 'stavrolen27383',
        data: filteredData.map(item => parsePrice(item.stavrolen27383)),
        backgroundColor: 'rgba(128, 128, 128, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['stavrolen27383']
      },
      {
        label: 'plast02651',
        data: filteredData.map(item => parsePrice(item.plast02651)),
        backgroundColor: 'rgba(139, 69, 19, 0.8)',
        type: 'bar' as const,
        yAxisID: 'y',
        datalabels: {
          display: false
        },
        hidden: !datasetVisibility['plast02651']
      }
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        min: 70000,
        max: 300000,
        ticks: {
          stepSize: 30000,
          callback: function (this: Scale<CoreScaleOptions>, tickValue: string | number) {
            return Number(tickValue).toLocaleString() + ' ₽';
          }
        },
      },
      x: {
        ticks: {
          callback: function (value: any, index: number) {
            const years = Array.from(new Set(filteredData.map(item =>
              new Date(item.date).getFullYear()
            )));

            const dataPointsPerYear = filteredData.length / years.length;
            const yearIndex = Math.round(index / dataPointsPerYear);

            if (years[yearIndex] && index % Math.round(dataPointsPerYear) === 0) {
              return years[yearIndex];
            }
            return '';
          },
          min: 2020,
          max: 2024,
          autoSkip: false
        },
        grid: {
          display: true,
          drawOnChartArea: true
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed?.y;
            if (value === null || value === undefined) {
              return `${label}: N/A`;
            }
            return `${label}: ${value.toLocaleString()} ₽`;
          }
        }
      },
      datalabels: {
        display: false
      },
      legend: {
        onClick: (e: any, legendItem: any) => {
          const datasetKey = legendItem.text;
          toggleDatasetVisibility(datasetKey);
        }
      }
    }
  };

  return (
    <div className="chart-container line-chart-container">
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '10px',
        gap: '10px'
      }}>
        <button
          onClick={toggleAllDatasets}
          style={{
            padding: '8px 16px',
            backgroundColor: showAllData ? '#f44336' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {showAllData ? 'Скрыть все' : 'Показать все'}
        </button>
      </div>
      <div style={{
        flex: 1,
        position: 'relative',
        minHeight: '500px',
        width: '100%',
        margin: '0 auto'
      }}>
        <Chart type='bar' data={chartData} options={options} />
      </div>
      <div
        ref={timelineRef}
        style={{
          backgroundColor: '#f0f0f0',
          position: 'relative',
          height: '30px',

        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            position: 'absolute',
            left: `${selectedRange[0] * 100}%`,
            right: `${(1 - selectedRange[1]) * 100}%`,
            top: 0,
            bottom: 0,
            backgroundColor: 'rgba(75, 192, 192, 0.3)',
            border: '2px solid rgb(75, 192, 192)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${selectedRange[0] * 100}%`,
            top: 0,
            bottom: 0,
            width: '10px',
            backgroundColor: 'rgb(75, 192, 192)',
            cursor: 'ew-resize',
          }}
          onMouseDown={(e) => handleMouseDown(e, 'left')}
        />
        <div
          style={{
            position: 'absolute',
            right: `${(1 - selectedRange[1]) * 100}%`,
            top: 0,
            bottom: 0,
            width: '10px',
            backgroundColor: 'rgb(75, 192, 192)',
            cursor: 'ew-resize',
          }}
          onMouseDown={(e) => handleMouseDown(e, 'right')}
        />
      </div>
    </div>
  );
};

export default DynamicsPe;
